import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectorRef, Component, effect, inject } from '@angular/core';
import { ModalComponent, ModalWrapperComponent } from '@catch/general-ui';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { Store } from '@ngrx/store';
import { FontawesomeService } from '../../services/fontawesome.service';
import { ReleaseNotesModalService } from '../../services/release-notes-modal.service';
import { clearReleaseNotesAfterLogin } from '../../store/auth/auth.actions';
import { selectShowReleaseNotesAfterLogin } from '../../store/auth/auth.reducer';
import { MeshGradientComponent } from '../mesh-gradient/mesh-gradient.component';

enum ReleaseNoteSection {
  FUNCTIONS = 'functions',
  IMPROVEMENTS = 'improvements',
  FIXES = 'fixes',
}

@Component({
  selector: 'app-release-notes-modal',
  templateUrl: './release-notes-modal.component.html',
  styleUrls: ['./release-notes-modal.component.scss'],
  //changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ModalComponent,
    TranslocoPipe,
    FontAwesomeModule,
    NgTemplateOutlet,
    MeshGradientComponent,
  ],
})
export class ReleaseNotesModalComponent extends ModalWrapperComponent {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly store = inject(Store);
  private readonly releaseNotesModalService = inject(ReleaseNotesModalService);
  private readonly translocoService = inject(TranslocoService);

  readonly ReleaseNoteSection = ReleaseNoteSection;
  fontawesomeService = inject(FontawesomeService);

  private readonly showReleaseNotesAfterLogin = this.store.selectSignal(
    selectShowReleaseNotesAfterLogin,
  );
  private readonly delay = 2000;
  private autoOpenHandled = false;

  /**
   * Returns the HTML for a release-notes section, or an empty string when the translation is missing or empty.
   * Transloco treats empty strings as missing keys and returns the key itself.
   * @param version The release version key, e.g. `v2-10-0`.
   * @param section The section to resolve.
   * @returns The translated HTML, or an empty string if there is no content.
   */
  getReleaseNoteSection(version: string, section: ReleaseNoteSection): string {
    const translationKey = `company-tool.snippets.release-notes-modal.${version}.${section}`;
    const translation = this.translocoService.translate(translationKey);

    if (!translation || translation === translationKey) {
      return '';
    }

    return translation;
  }

  constructor() {
    super();
    this.releaseNotesModalService.register(this);

    effect(() => {
      if (this.autoOpenHandled || !this.showReleaseNotesAfterLogin()) {
        return;
      }

      this.autoOpenHandled = true;
      this.store.dispatch(clearReleaseNotesAfterLogin());
      setTimeout(() => {
        this.open();
        this.cdr.markForCheck();
      }, this.delay);
    });
  }
}
